import React, { Dispatch } from 'react';
import I18n from 'i18n-js';

import { gql } from '@reverbdotcom/commons/src/gql';

import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { SanitizedRender } from '@reverbdotcom/commons';
import { RCModal } from '@reverbdotcom/cadence/components';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';

import { Listing, SellerDashboardBulkAction } from '@reverbdotcom/commons/src/gql/graphql';
import SellerListingCardsToolbarContext from '../../SellerListingCardsToolbarContext';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import SellerListingsCollectionContext from '../../SellerListingsCollectionContext';
import { SELLER_DASHBOARD_BULK_ACTION_MUTATION } from '../sellerDashboardBulkActionMutation';

export const COMPONENT_NAME = 'BulkDeleteBumpModal';

const BULK_DELETE_BUMPED_MUTATION = gql(`
  mutation Core_MyListings_BulkDeleteBumped(
    $input: Input_core_apimessages_BulkDeleteBumpedRequest
  ) {
    bulkDeleteBumped(input: $input) {
      responses {
        listingId
        success
        errors {
          message
        }
      }
    }
  }
`);

const BULK_DELETE_BUMPED_ASYNC_MUTATION = gql(`
  mutation Core_MyListings_BulkDeleteBumpedAsync(
    $input: Input_core_apimessages_BulkDeleteBumpedAsyncRequest
  ) {
    bulkDeleteBumpedAsync(input: $input) {
      enqueued
      message
    }
  }
`);

interface IExternalProps {
  closeModal: Dispatch<void>;
  selectedListings: Listing[];
}

export default function BulkDeleteBumpModal({
  closeModal,
  selectedListings,
}: IExternalProps) {
  const {
    listingsCollectionState,
  } = React.useContext(SellerListingsCollectionContext);

  const {
    hasAllMatchesSelected,
    totalMatchesEnabledForBump,
    serializedDashboardSearchParams,
    searchUrlParams,
  } = listingsCollectionState;

  const {
    handleBulkResponses,
    handleGenericError,
    handleAsyncBulkResponse,
  } = React.useContext(SellerListingCardsToolbarContext);

  const [bulkDeleteBumped, {
    data,
    loading,
    errors,
  }] = useMutation(BULK_DELETE_BUMPED_MUTATION);
  const hasErrors = !!errors.length;

  const [bulkDeleteBumpedAsync, {
    data: asyncData,
    loading: asyncLoading,
    errors: asyncErrors,
  }] = useMutation(BULK_DELETE_BUMPED_ASYNC_MUTATION);

  const [sellerDashboardBulkAction, {
    data: bulkActionData,
    loading: bulkActionLoading,
    errors: bulkActionErrors,
  }] = useMutation(SELLER_DASHBOARD_BULK_ACTION_MUTATION);
  const hasAsyncErrors = !!asyncErrors.length || !!bulkActionErrors.length;

  const enabledListings = selectedListings.filter(listing => !!listing.bumpRate?.rate);

  const enabledCount = hasAllMatchesSelected ? totalMatchesEnabledForBump : enabledListings.length;

  React.useEffect(() => {
    if (loading) return;
    if (hasErrors) return handleUnexpectedError();

    handleResponses();
  }, [loading]);

  React.useEffect(() => {
    if (asyncLoading || bulkActionLoading) return;
    if (hasAsyncErrors) return handleUnexpectedError();

    handleAsyncResponse();
  }, [asyncLoading, bulkActionLoading]);

  function handleResponses() {
    if (data?.bulkDeleteBumped) {
      const { responses } = data.bulkDeleteBumped;

      handleBulkResponses({
        responses,
        I18nSuccessMessageKey: 'discovery.listingsManagement.toolbar.bulkActions.bump.deleteModal.successes',
        I18nErrorMessageKey: 'discovery.listingsManagement.toolbar.bulkActions.bump.deleteModal.errors.title',
      });

      closeModal();
    }
  }

  function handleAsyncResponse() {
    if (asyncData?.bulkDeleteBumpedAsync || bulkActionData?.sellerDashboardBulkAction) {
      handleAsyncBulkResponse({
        I18nKey: 'discovery.listingsManagement.toolbar.bulkActions.bump.deleteModal.asyncResponses.successes',
        expectedTotal: enabledCount,
      });

      closeModal();
    }
  }

  function handleUnexpectedError() {
    handleGenericError();
    closeModal();
  }

  function submit() {
    trackEvent({
      componentName: COMPONENT_NAME,
      eventName: MParticleEventName.SellerBulkDeletingBump,
      listingsCount: enabledCount,
      hasAllMatchesSelected,
    });

    if (hasAllMatchesSelected && searchUrlParams !== undefined) {
      sellerDashboardBulkAction({
        variables: {
          input: {
            action: SellerDashboardBulkAction.BUMP_REMOVE,
            expectedTotal: enabledCount,
            searchUrlParams,
          },
        },
      });
    } else if (hasAllMatchesSelected) {
      bulkDeleteBumpedAsync({
        variables: {
          input: {
            expectedTotal: enabledCount,
            serializedDashboardSearchParams,
          },
        },
      });

    } else {
      const listingIds = enabledListings.map(listing => listing.id);

      bulkDeleteBumped({
        variables: {
          input: {
            listingIds,
          },
        },
      });
    }
  }

  return (
    <RCModal
      isOpen
      onOpenChange={(open) => { if (!open) closeModal(); }}
      title={I18n.t('discovery.listingsManagement.toolbar.bulkActions.bump.deleteModal.headerTitle')}
      actions={{
        primary: {
          buttonText: I18n.t('discovery.listingsManagement.toolbar.bulkActions.bump.deleteModal.submit'),
          disabled: !enabledCount,
          loading: loading || asyncLoading || bulkActionLoading,
          onClick: submit,
        },
        secondary: true,
      }}
    >
      <div className="d-flex fx-dir-col gap-4">
        <Translate
          text="discovery.listingsManagement.toolbar.bulkActions.bump.deleteModal.buyerText"
          html
        />

        <div className="d-flex fx-dir-col gap-2">
          <SanitizedRender
            html={
              I18n.t('discovery.listingsManagement.toolbar.bulkActions.bump.deleteModal.disableText', {
                count: enabledCount,
                formattedCount: I18n.toNumber(enabledCount, {
                  precision: 0,
                }),
              })
            }
          />
        </div>
      </div>
    </RCModal>
  );
}
